import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchAllRewards, updateReward } from '../../api/rewards';
import useSeries from '../../hooks/useSeries';

const ViewRewardsPage = () => {
    const [rewards, setRewards] = useState([]);
    const [editingRewardID, setEditingRewardID] = useState(null);
    const [editableRewardData, setEditableRewardData] = useState({});
    const series = useSeries();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setError(null);
            try {
                const rewards = await fetchAllRewards();
                setRewards(rewards);
            } catch (error) {
                setError('Error fetching rewards');
                toast.error('Error fetching rewards: ' + error.message);
            }
        };

        fetchData();
    }, []);

    const handleEditClick = reward => {
        setEditingRewardID(reward.rewardID);
        setEditableRewardData(reward); // Set the reward's current data into the form
    };

    const handleCancelClick = () => {
        setEditingRewardID(null);
        setEditableRewardData({});
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setEditableRewardData(prevData => ({ ...prevData, [name]: value }));
    };

    const handleUpdateClick = async rewardID => {
        setLoading(true);
        try {
            await updateReward(rewardID, editableRewardData);
            toast.success('Reward updated successfully!');
            setEditingRewardID(null); // Reset the editing mode
            // Update the rewards list with the updated data
            setRewards(prevRewards =>
                prevRewards.map(reward =>
                    reward.rewardID === rewardID ? { ...reward, ...editableRewardData } : reward
                )
            );
        } catch (error) {
            toast.error('Error updating reward: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return <p className="error">{error}</p>;
    }

    return (
        <div className="admin-page view-rewards-page">
            <h2>Rewards</h2>
            <div style={{ marginBottom: 16 }}>
                <Link to="/rewards/create" className="btn-primary">+ Create Reward</Link>
            </div>
            {rewards.length === 0 ? (
                <div className="admin-empty">No rewards found.</div>
            ) : (
                <div className="table-scroll-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Money</th>
                                <th>Series</th>
                                <th>Packs</th>
                                <th>Card</th>
                                <th>Series %</th>
                                <th>Card %</th>
                                <th>Description</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rewards.map(reward => (
                                <tr key={reward.rewardID}>
                                    {editingRewardID === reward.rewardID ? (
                                        <>
                                            <td>{reward.rewardID}</td>
                                            <td><div className="cell-action"><input type="number" name="money" value={editableRewardData.money} onChange={handleChange} /></div></td>
                                            <td>
                                                <div className="cell-action">
                                                    <select name="seriesID" value={editableRewardData.seriesID} onChange={handleChange}>
                                                        <option value="">--</option>
                                                        {series.map(s => (
                                                            <option key={s.seriesID} value={s.seriesID}>{s.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </td>
                                            <td><div className="cell-action"><input type="number" name="numberOfSeries" value={editableRewardData.numberOfSeries || ''} onChange={handleChange} /></div></td>
                                            <td><div className="cell-action"><input type="number" name="cardID" value={editableRewardData.cardID || ''} onChange={handleChange} /></div></td>
                                            <td><div className="cell-action"><input type="number" name="seriesPct" value={editableRewardData.seriesPct || ''} onChange={handleChange} /></div></td>
                                            <td><div className="cell-action"><input type="number" name="cardPct" value={editableRewardData.cardPct || ''} onChange={handleChange} /></div></td>
                                            <td><div className="cell-action"><input type="text" name="description" value={editableRewardData.description || ''} onChange={handleChange} /></div></td>
                                            <td>
                                                <div className="actions-cell">
                                                    <button onClick={() => handleUpdateClick(reward.rewardID)} disabled={loading}>
                                                        {loading ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button onClick={handleCancelClick}>Cancel</button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{reward.rewardID}</td>
                                            <td>{reward.money}</td>
                                            <td>{reward.Series?.name}</td>
                                            <td>{reward.numberOfSeries || ''}</td>
                                            <td>{reward.Card ? `${reward.Card.year} ${reward.Card.name}` : ''}</td>
                                            <td>{reward.seriesPct}</td>
                                            <td>{reward.cardPct}</td>
                                            <td>{reward.description}</td>
                                            <td>
                                                <div className="actions-cell">
                                                    <button onClick={() => handleEditClick(reward)}>Edit</button>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ViewRewardsPage;
